import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Building, PlusCircle, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useFixedAssetDetail } from '@/hooks/useFixedAssetDetail';
import { useFixedAssetValues } from '@/hooks/useFixedAssetValues';
import { useApartmentDetail } from '@/hooks/useApartmentDetail';
import { useRentPayments } from '@/hooks/useRentPayments';
import { AssetCoreInfo } from '@/pages/fixedAssets/components/fixed-asset-detail/AssetCoreInfo';
import { AssetTypeSpecificInfo } from '@/pages/fixedAssets/components/fixed-asset-detail/AssetTypeSpecificInfo';
import { ValueHistoryChart } from '@/pages/fixedAssets/components/fixed-asset-detail/ValueHistoryChart';
import { ValueRecordingForm } from '@/pages/fixedAssets/components/fixed-asset-detail/ValueRecordingForm';
import { UpcomingRentSchedule } from '@/pages/fixedAssets/components/fixed-asset-detail/UpcomingRentSchedule';
import { RentPaymentForm } from '@/pages/fixedAssets/components/fixed-asset-detail/RentPaymentForm';
import { RentPaymentHistory } from '@/pages/fixedAssets/components/fixed-asset-detail/RentPaymentHistory';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useUpdateFixedAsset, FixedAsset } from '@/hooks/useFixedAssets';
import { FixedAssetForm } from '@/pages/fixedAssets/components/fixed-asset-form/FixedAssetForm';
import type { FixedAssetFormData } from '@/pages/fixedAssets/components/fixed-asset-form/fixedAssetForm.schema';
import { PropertyPurchaseProgress } from '@/pages/fixedAssets/components/fixed-asset-detail/PropertyPurchaseProgress';
import { GroundRentSection } from '@/pages/fixedAssets/components/fixed-asset-detail/GroundRentSection';
import { PropertyTaxSection } from '@/pages/fixedAssets/components/fixed-asset-detail/PropertyTaxSection';
import { LegalFeesSection } from '@/pages/fixedAssets/components/fixed-asset-detail/legal-fees/LegalFeesSection';

async function uploadOwnershipDocument(userId: string, assetId: string, file: File): Promise<string> {
  const path = `${userId}/${assetId}/${file.name}`;
  const { error } = await supabase.storage.from('ownership-documents').upload(path, file, { upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from('ownership-documents').getPublicUrl(path);
  return data.publicUrl;
}

export default function FixedAssetDetailPage() {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const [recordValueOpen, setRecordValueOpen] = useState(false);
  const [recordRentOpen, setRecordRentOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { user } = useAuth();
  const updateAsset = useUpdateFixedAsset();

  const {
    data: detail,
    isLoading,
    isError,
  } = useFixedAssetDetail(assetId);

  const { data: values = [] } = useFixedAssetValues(assetId);

  const isApartment = detail?.asset.asset_type === 'apartment';
  const { data: apartment } = useApartmentDetail(isApartment ? assetId : undefined);
  const { data: rentPayments = [] } = useRentPayments(apartment?.id);

  const handleSaveAsset = async (data: FixedAssetFormData, file: File | null) => {
    if (!detail?.asset || !user) return;
    
    const basePayload = {
      name: data.name,
      asset_type: data.asset_type,
      location: data.location || null,
      purchase_price: data.purchase_price,
      purchase_date: data.purchase_date || null,
      value: data.value,
      currency: data.currency,
      status: data.status ?? 'owned',
      notes: data.notes || null,
      ownership_document_url: data.ownership_document_url || null,
      tenure_type: data.tenure_type || null,
      ownership_status: data.ownership_status ?? 'fully_owned',
      amount_paid: data.ownership_status === 'purchasing' ? (data.amount_paid ?? 0) : data.purchase_price,
    };

    let docUrl = basePayload.ownership_document_url;
    if (file) {
      try {
        docUrl = await uploadOwnershipDocument(user.id, detail.asset.id, file);
      } catch {
        toast.error('Failed to upload ownership document');
        return;
      }
    }

    await updateAsset.mutateAsync({ id: detail.asset.id, ...basePayload, ownership_document_url: docUrl });

    if (data.asset_type === 'apartment') {
      const { error: aptError } = await supabase
        .from('apartments')
        .update({
          apartment_number: data.apartment_number!,
          expected_rent: data.expected_rent!,
          service_fee: data.service_fee ?? 0,
          management_fee: data.management_fee ?? 0,
          management_fee_vat: data.management_fee_vat ?? 0,
          rent_day_of_month: data.rent_day_of_month!,
          is_occupied: data.is_occupied ?? false,
        })
        .eq('fixed_asset_id', detail.asset.id);
      if (aptError) {
        toast.error(`Failed to update apartment details: ${aptError.message}`);
        return;
      }
    } else if (data.asset_type === 'land') {
      const { error: landError } = await supabase
        .from('lands')
        .update({ acres: data.acres! })
        .eq('fixed_asset_id', detail.asset.id);
      if (landError) {
        toast.error(`Failed to update land details: ${landError.message}`);
        return;
      }
    } else if (data.asset_type === 'house') {
      const { error: houseError } = await supabase
        .from('houses')
        .update({ land_id: data.land_id ?? null })
        .eq('fixed_asset_id', detail.asset.id);
      if (houseError) {
        toast.error(`Failed to update house details: ${houseError.message}`);
        return;
      }
    }

    toast.success('Asset updated');
    setEditDialogOpen(false);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-4xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !detail) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-4xl" data-testid="not-found">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <Building className="h-12 w-12 text-muted-foreground" />
            <p className="text-lg font-medium">Asset not found</p>
            <p className="text-sm text-muted-foreground">
              The asset you're looking for doesn't exist or you don't have access to it.
            </p>
            <Button asChild variant="outline">
              <Link to="/fixed-assets">Back to Fixed Assets</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/fixed-assets')}
          aria-label="Back to fixed assets"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold truncate">{detail.asset.name}</h1>
          <p className="text-sm text-muted-foreground capitalize">
            {detail.asset.asset_type.replace(/_/g, ' ')}
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => setEditDialogOpen(true)}
          data-testid="edit-asset-button"
        >
          <Pencil className="h-4 w-4 mr-2" />
          Edit Asset
        </Button>
      </div>

      {/* Core info card */}
      <AssetCoreInfo asset={detail.asset} values={values} />

      {/* Property Purchase Progress - Shown if ownership_status is 'purchasing' */}
      {detail.asset.ownership_status === 'purchasing' && (
        <PropertyPurchaseProgress asset={detail.asset} />
      )}

      {/* Legal Fees Section - Req 4.1, 4.2, 4.3, 8.2, 8.3 */}
      {detail.asset.ownership_status === 'purchasing' && (
        <LegalFeesSection asset={detail.asset as FixedAsset} />
      )}

      {/* Type-specific info card */}
      <AssetTypeSpecificInfo detail={detail} />

      {/* Ground Rent Section - Shown for all property types */}
      {['apartment', 'house', 'land', 'commercial_building'].includes(detail.asset.asset_type) && (
        <GroundRentSection asset={detail.asset} />
      )}

      {/* Property Tax Section - Shown only for leasehold properties */}
      {detail.asset.tenure_type === 'leasehold' && (
        <PropertyTaxSection asset={detail.asset} />
      )}

      {/* Upcoming rent schedule — apartment only, when occupied */}
      {isApartment && apartment && (
        <UpcomingRentSchedule apartment={apartment} rentPayments={rentPayments} />
      )}

      {/* Record Rent Payment button — apartment only */}
      {isApartment && apartment && (
        <div className="flex justify-end">
          <Button onClick={() => setRecordRentOpen(true)} data-testid="record-rent-button">
            <PlusCircle className="h-4 w-4 mr-2" />
            Record Rent Payment
          </Button>
        </div>
      )}

      {/* Rent payment history — apartment only */}
      {isApartment && apartment && (
        <RentPaymentHistory rentPayments={rentPayments} />
      )}

      {/* Value history section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>Value Tracking</CardTitle>
          <Button
            size="sm"
            onClick={() => setRecordValueOpen(true)}
            data-testid="record-value-button"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Record Value
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <ValueHistoryChart values={values} currency={detail.asset.currency ?? 'USD'} />
        </CardContent>
      </Card>

      {/* Record Value Dialog */}
      <Dialog open={recordValueOpen} onOpenChange={setRecordValueOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Asset Value</DialogTitle>
          </DialogHeader>
          <ValueRecordingForm
            assetId={assetId!}
            onSuccess={() => setRecordValueOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Record Rent Payment Dialog */}
      {isApartment && apartment && (
        <Dialog open={recordRentOpen} onOpenChange={setRecordRentOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Rent Payment</DialogTitle>
            </DialogHeader>
            <RentPaymentForm
              apartmentId={apartment.id}
              expectedRent={Number(apartment.expected_rent)}
              rentDayOfMonth={apartment.rent_day_of_month}
              onSuccess={() => setRecordRentOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Asset Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="w-[95vw] sm:w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Asset</DialogTitle>
          </DialogHeader>
          <FixedAssetForm
            asset={detail.asset as unknown as FixedAsset}
            onSave={handleSaveAsset}
            onCancel={() => setEditDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
